import {
  AddToListRequest,
  ApolloListCreate,
  FacebookListCreate,
  GetListRequest,
  InstagramCommentsListCreate,
  InstagramDetailsListCreate,
  InstagramListCreate,
  LinkedInListCreate,
  TList,
  TwilioListCreate,
  UpdateListRequest,
  WhatsAppListCreate,
} from "@/types/listTypes";
import {createApi, FetchArgs} from "@reduxjs/toolkit/query/react";
import platformAuthConfig from "./platformConfig";

export type TagTypes = "Lists" | "ListItems";

const listBuildingApi = createApi({
  reducerPath: "listBuildingApi",
  ...platformAuthConfig(),
  tagTypes: ["Lists", "ListItems"],
  endpoints: (builder) => ({
    createInstagramList: builder.mutation<TList[], InstagramListCreate>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/instagram`,
        method: "POST",
        body: config,
      }),
      invalidatesTags: ["Lists"],
    }),
    createInstagramCommentsList: builder.mutation<
      TList[],
      InstagramCommentsListCreate
    >({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/instagram/comments`,
        method: "POST",
        body: config,
      }),
      invalidatesTags: ["Lists"],
    }),

    createApolloList: builder.mutation<TList[], ApolloListCreate>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/apollo`,
        method: "POST",
        body: config,
      }),
      invalidatesTags: ["Lists"],
    }),

    createFacebookList: builder.mutation<TList[], FacebookListCreate>({
      query: (config): FetchArgs => {
        const formData = new FormData();

        // Add string data
        formData.append("name", config.name);
        formData.append("url", config.url);
        if (config.cookies_file) {
          formData.append("cookies_file", config.cookies_file);
        }

        return {
          url: `/organizations/${config.org_id}/list_building/facebook/group`,
          method: "POST",
          body: formData,
          // formData: true,
        };
      },
      invalidatesTags: ["Lists"],
    }),

    createWhatAppList: builder.mutation<TList[], WhatsAppListCreate>({
      query: (config): FetchArgs => {
        const formData = new FormData();

        // Add string data
        formData.append("name", config.name);
        if (config.contacts_file) {
          formData.append("contacts_file", config.contacts_file);
        }

        return {
          url: `/organizations/${config.org_id}/list_building/whatsapp`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Lists"],
    }),

    createLinkedInList: builder.mutation<TList[], LinkedInListCreate>({
      query: (config): FetchArgs => {
        const formData = new FormData();

        // Add string data
        formData.append("name", config.name);
        if (config.linkedin_details) {
          formData.append("linkedin_details", config.linkedin_details);
        }

        return {
          url: `/organizations/${config.org_id}/list_building/linkedin-upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Lists"],
    }),

    createInstagramDetailsList: builder.mutation<
      TList[],
      InstagramDetailsListCreate
    >({
      query: (config): FetchArgs => {
        const formData = new FormData();

        // Add string data
        formData.append("name", config.name);
        if (config.instagram_details) {
          formData.append("instagram_details", config.instagram_details);
        }

        return {
          url: `/organizations/${config.org_id}/list_building/instagram-upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Lists"],
    }),

    createTwilioList: builder.mutation<TList[], TwilioListCreate>({
      query: (config): FetchArgs => {
        const formData = new FormData();

        // Add string data
        formData.append("name", config.name);
        if (config.contacts_file) {
          formData.append("contacts_file", config.contacts_file);
        }

        return {
          url: `/organizations/${config.org_id}/list_building/twilio-upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Lists"],
    }),

    deleteList: builder.mutation<void, GetListRequest>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/lists/${config.list_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Lists"],
    }),
    getLists: builder.query<TList[], string>({
      query: (org_id): FetchArgs => ({
        url: `/organizations/${org_id}/list_building/lists`,
        method: "GET",
      }),
      providesTags: ["Lists"],
    }),
    getList: builder.query<TList, GetListRequest>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/lists/${config.list_id}`,
        method: "GET",
      }),
      providesTags: ["ListItems"],
    }),
    updateList: builder.mutation<TList, UpdateListRequest>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/lists/${config.list_id}`,
        method: "PUT",
        body: config,
      }),
      invalidatesTags: ["ListItems"],
    }),
    addToList: builder.mutation<TList, AddToListRequest>({
      query: (config): FetchArgs => ({
        url: `/organizations/${config.org_id}/list_building/add_leads/${
          config.list_id
        }?platform=${config.social_media_type.toLowerCase()}`,
        method: "POST",
        body: config.items,
      }),
      invalidatesTags: ["ListItems"],
    }),
  }),
});

export const {
  useCreateApolloListMutation,
  useCreateInstagramCommentsListMutation,
  useCreateInstagramListMutation,
  useCreateFacebookListMutation,
  useCreateWhatAppListMutation,
  useCreateLinkedInListMutation,
  useCreateInstagramDetailsListMutation,
  useCreateTwilioListMutation,
  useGetListsQuery,
  useGetListQuery,
  useDeleteListMutation,
  useUpdateListMutation,
  useAddToListMutation,
} = listBuildingApi;

export default listBuildingApi;
